import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import { Alert, Box, Divider, Grid, Typography } from "@mui/material";
import type { FilterEntry } from "hooks/filters";
import type { ColorSearchSettings } from "models/colors";
import { type Dispatch, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationClassFilter } from "./ApplicationClassFilter";
import { ApplicationFilter } from "./ApplicationFilter";
import { BrandFilter } from "./BrandFilter";
import { GradeFilter } from "./GradeFilter";
import { GradeShortNameFilter } from "./GradeShortName";
import { IndustryFilter } from "./IndustryFilter";
import { NumberFilter } from "./NumberFilter";
import { PropertiesFilter } from "./PropertiesFilter";
import { type UseCaseOption, UseCaseSelect } from "./UseCaseFilter";

export interface MaterialFilterProps {
  filters?: Array<FilterEntry>;
  brands?: Array<AutocompleteData>;
  onBrandsChange?: Dispatch<Array<AutocompleteData>>;
  grades?: Array<AutocompleteData>;
  onGradesChange?: Dispatch<Array<AutocompleteData>>;
  industry?: Array<AutocompleteData>;
  applicationClass?: Array<AutocompleteData>;
  application?: Array<AutocompleteData>;
  gradeNumbers?: Array<AutocompleteData>;
  onGradeNumbersChange?: Dispatch<Array<AutocompleteData>>;
  gradeProperties?: Array<AutocompleteData>;
  onGradePropertiesChange?: Dispatch<Array<AutocompleteData>>;
  columns?: number;
  onIndustryChange?: Dispatch<Array<AutocompleteData>>;
  onApplicationClassChange?: Dispatch<Array<AutocompleteData>>;
  onApplicationChange?: Dispatch<Array<AutocompleteData>>;
  isLandingPage?: boolean;
  settings: ColorSearchSettings;
  gradeShortName?: Array<AutocompleteData>;
  onGradeShortNameChange?: Dispatch<Array<AutocompleteData>>;
  isInternal: boolean;
}

const materialFilterCases: Record<string, UseCaseOption> = {
  covestro_SAP_short_text: {
    value: "covestro_SAP_short_text",
    label: "filter_SAP_short_text",
  },
  covestro_use_case: {
    value: "covestro_use_case",
    label: "filter_use_case",
  },
  covestro_material_grade: {
    value: "covestro_material_grade",
    label: "filter_material_grade",
  },
};

export const MaterialFilter = ({
  filters = [],
  brands,
  grades,
  gradeNumbers,
  gradeProperties,
  industry,
  applicationClass,
  application,
  onBrandsChange,
  onGradeNumbersChange,
  onGradePropertiesChange,
  onGradesChange,
  columns = 12,
  onIndustryChange,
  onApplicationClassChange,
  onApplicationChange,
  isLandingPage = false,
  settings,
  gradeShortName,
  onGradeShortNameChange,
  isInternal,
}: MaterialFilterProps) => {
  const { t } = useTranslation();

  const hasMaterialGrade =
    filters.some((filter) => filter.type === "grade") ||
    settings.materialSection === "materialGrade";
  const hasUseCase =
    filters.some((filter) => filter.type === "industry") ||
    settings.materialSection === "industryUseCase";
  const [selectedUseCase, setSelectedUseCase] = useState(
    !hasMaterialGrade && !hasUseCase
      ? materialFilterCases.covestro_SAP_short_text.value
      : hasUseCase
        ? materialFilterCases.covestro_use_case.value
        : hasMaterialGrade
          ? materialFilterCases.covestro_material_grade.value
          : materialFilterCases.none.value,
  );

  const onSelectUseCaseChange = (useCaseValue: string) => {
    setSelectedUseCase(useCaseValue);
  };

  return (
    <Grid mt={-2} mb={2} container spacing={2} columns={columns}>
      <Grid size={{ xs: 12, md: 12, xl: 3 }}>
        <UseCaseSelect
          onIndustryChange={onIndustryChange}
          options={
            isInternal
              ? Object.values(materialFilterCases)
              : Object.values(materialFilterCases).slice(1)
          }
          onChange={onSelectUseCaseChange}
          value={selectedUseCase}
          t={t}
        />
      </Grid>
      {selectedUseCase ===
        materialFilterCases.covestro_SAP_short_text.value && (
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          <GradeShortNameFilter
            value={gradeShortName}
            onChange={onGradeShortNameChange}
            t={t}
          />
        </Grid>
      )}
      {selectedUseCase === materialFilterCases.covestro_use_case.value && (
        <>
          <Grid size={{ xs: 12, md: 12, xl: 3 }}>
            <IndustryFilter
              value={industry}
              onChange={onIndustryChange}
              t={t}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 12, xl: 3 }}
            sx={{ display: "inline-flex", position: "relative" }}
          >
            <Grid size={{ xs: 1, md: 1, xl: 1 }}>
              <Box
                sx={{
                  borderLeft: "1px solid #CAD2D6",
                  boxSizing: "border-box",
                  height: "26px",
                  width: "13.5px",
                  marginLeft: "10px",
                  position: "absolute",
                  top: "12px",
                  borderBottom: "1px solid #CAD2D6",
                }}
              />
            </Grid>
            <Grid size={{ xs: 11, md: 11, xl: 11 }}>
              <ApplicationClassFilter
                isDisabled={!industry || industry.length === 0}
                value={applicationClass}
                onChange={onApplicationClassChange}
                industry={industry}
                t={t}
              />
            </Grid>
          </Grid>
          <Grid
            size={{ xs: 12, md: 12, xl: 3 }}
            sx={{ display: "inline-flex", position: "relative" }}
          >
            <Grid size={{ xs: 1, md: 1, xl: 1 }}></Grid>
            <Grid size={{ xs: 1, md: 1, xl: 1 }}>
              <Box
                sx={{
                  borderLeft: "1px solid #CAD2D6",
                  boxSizing: "border-box",
                  height: "26px",
                  width: "13.5px",
                  marginLeft: "10px",
                  position: "absolute",
                  top: "12px",
                  borderBottom: "1px solid #CAD2D6",
                }}
              />
            </Grid>
            <Grid size={{ xs: 10, md: 10, xl: 10 }}>
              <ApplicationFilter
                isDisabled={!applicationClass || applicationClass.length === 0}
                value={application}
                onChange={onApplicationChange}
                industry={industry}
                applicationClass={applicationClass}
                t={t}
              />
            </Grid>
          </Grid>
          {!isLandingPage && (
            <Grid size={{ xs: 12, md: 12, xl: 3 }}>
              <Alert severity="info" sx={{ textAlign: "left" }}>
                {t("filter_material_alert_text")}
              </Alert>
            </Grid>
          )}
        </>
      )}
      {selectedUseCase ===
        materialFilterCases.covestro_material_grade.value && (
        <>
          <Grid size={{ xs: 12, md: 12, xl: 3 }}>
            <GradeFilter
              value={grades}
              onChange={onGradesChange}
              t={t}
              industry={industry}
              applicationClass={applicationClass}
              application={application}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, xl: 3 }}>
            <Divider>
              <Typography variant={"overline"}>OR</Typography>
            </Divider>
          </Grid>
          <Grid size={{ xs: 12, md: 6, xl: 3 }}>
            <BrandFilter value={brands} onChange={onBrandsChange} t={t} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, xl: 3 }}>
            <NumberFilter
              t={t}
              value={gradeNumbers}
              onChange={onGradeNumbersChange}
              brands={brands}
              gradeProperties={gradeProperties}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, xl: 3 }}>
            <PropertiesFilter
              t={t}
              value={gradeProperties}
              onChange={onGradePropertiesChange}
              brands={brands}
              gradeNumbers={gradeNumbers}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
